import { Section } from '@/components/ui/Section';
import { Select } from '@/components/ui/Select';
import type { ElementStyle } from '@/lib/schema.types';

interface Props {
  /** Effective style at the active breakpoint (base merged with overrides). */
  style: ElementStyle;
  /** Apply a style delta (breakpoint-aware — handled by the caller). */
  onStyle: (patch: Partial<ElementStyle>) => void;
}

const SHADOWS: { label: string; value: string }[] = [
  { label: 'None', value: '' },
  { label: 'Small', value: '0 1px 2px rgba(0,0,0,0.08)' },
  { label: 'Medium', value: '0 4px 12px rgba(0,0,0,0.12)' },
  { label: 'Large', value: '0 10px 30px rgba(0,0,0,0.18)' },
];

/**
 * Advanced element chrome: border, opacity, shadow, and a raw custom-CSS
 * escape hatch. All fields are standard CSS so the classroom can apply them
 * directly (parity spec §13).
 */
export function ElementAdvancedProperties({ style, onStyle }: Props) {
  const borderWidth = Number(style.borderWidth ?? 0) || 0;
  const borderStyle = (style.borderStyle as string | undefined) ?? 'solid';
  const borderColor = (style.borderColor as string | undefined) ?? '#1f2937';
  const opacity = style.opacity != null ? Number(style.opacity) : 1;
  const boxShadow = (style.boxShadow as string | undefined) ?? '';
  const customCss = (style.customCss as string | undefined) ?? '';

  return (
    <>
      <Section title="Border">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Width (px)">
            <input
              type="number"
              min={0}
              value={borderWidth}
              onChange={(e) => onStyle({ borderWidth: Number(e.target.value) || 0 })}
              className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </Field>
          <Field label="Style">
            <Select value={borderStyle} onChange={(e) => onStyle({ borderStyle: e.target.value })}>
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="dotted">Dotted</option>
            </Select>
          </Field>
        </div>
        <Field label="Color">
          <ColorRow value={borderColor} onChange={(v) => onStyle({ borderColor: v })} />
        </Field>
      </Section>

      <Section title="Effects">
        <Field label={`Opacity — ${Math.round(opacity * 100)}%`}>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={opacity}
            onChange={(e) => onStyle({ opacity: Number(e.target.value) })}
            className="w-full"
          />
        </Field>
        <Field label="Shadow">
          <Select value={boxShadow} onChange={(e) => onStyle({ boxShadow: e.target.value })}>
            {SHADOWS.map((s) => (
              <option key={s.label} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </Field>
      </Section>

      <Section title="Custom CSS">
        <textarea
          value={customCss}
          onChange={(e) => onStyle({ customCss: e.target.value })}
          rows={4}
          spellCheck={false}
          placeholder={'e.g. letter-spacing: 0.5px;\nbackdrop-filter: blur(4px);'}
          className="block w-full resize-y rounded-md border border-border bg-white px-2 py-1.5 font-mono text-xs text-fg outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
        />
        <p className="mt-1 text-[10.5px] text-fg-subtle">
          Raw CSS declarations applied last. Applies in the editor and the
          classroom renderer.
        </p>
      </Section>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-fg-muted">{label}</span>
      {children}
    </label>
  );
}

function ColorRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex h-9 items-center gap-2 rounded-md border border-border bg-white pl-2 pr-3 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-6 w-7 cursor-pointer rounded-sm border border-border bg-transparent"
        aria-label="Pick color"
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent text-sm text-fg outline-none"
      />
    </div>
  );
}
